import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const DarkModeContext = createContext(null);

/**
 * Resolve the localStorage key for a given app.
 *
 * Apps don't share the same toggle state — recon's dark-mode preference
 * shouldn't clobber blotter's. We key by appName so each SWA gets its own
 * persisted choice.
 */
const storageKey = (appName) => `${appName || 'app'}-dark-mode`;

const readInitialDarkMode = (appName, defaultDark) => {
  if (typeof window === 'undefined' || !window.localStorage) return !!defaultDark;
  try {
    const stored = window.localStorage.getItem(storageKey(appName));
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch {
    // localStorage can throw under some privacy modes; fall through
  }
  return !!defaultDark;
};

/**
 * Build a theme for the given mode.
 *
 * The grey-palette overrides in dark mode are load-bearing: PamGrid (and any
 * MUI component that reaches into `theme.palette.grey[50/100/200]` directly)
 * uses static grey values that don't flip with the mode. We replace them with
 * dark tones here so the footer background, header background, and keyboard
 * pill backgrounds render correctly without each component knowing about it.
 */
const buildTheme = (darkMode, userThemeOptions) => {
  const userPalette = userThemeOptions?.palette || {};
  const userGrey = userPalette.grey || {};
  const userRest = { ...userThemeOptions };
  delete userRest.palette;

  return createTheme({
    ...userRest,
    palette: {
      ...userPalette,
      mode: darkMode ? 'dark' : 'light',
      ...(darkMode && {
        grey: {
          50: '#1e1e1e',
          100: '#2d2d2d',
          200: '#3a3a3a',
          300: '#555555',
          400: '#757575',
          ...userGrey,
        },
      }),
    },
  });
};

/**
 * DarkModeProvider — manages dark/light state, persists to localStorage, and
 * provides MUI theme + CssBaseline.
 *
 * Wrap your application once at the top:
 *
 *   <DarkModeProvider appName="recon">
 *     <NotificationProvider>
 *       <AuthLayout title="Recon">{...}</AuthLayout>
 *     </NotificationProvider>
 *   </DarkModeProvider>
 *
 * AuthLayout will pick up the context and render a toggle automatically.
 * PamGrid will pick up `theme.palette.mode === 'dark'` and apply dark-mode
 * styling automatically. Apps don't need to do anything else.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.appName] - Used as localStorage key prefix
 * @param {boolean} [props.defaultDark=false] - Initial mode when no stored value
 * @param {Object} [props.themeOptions] - Extra MUI createTheme options to merge.
 *   palette.mode is always overridden; palette.grey is merged on top of the
 *   dark-mode overrides so apps can fine-tune individual shades.
 */
export const DarkModeProvider = ({
  children,
  appName,
  defaultDark = false,
  themeOptions,
}) => {
  const [darkMode, setDarkModeState] = useState(() =>
    readInitialDarkMode(appName, defaultDark),
  );

  // Persist on change — but skip the initial commit so we don't write
  // "false" to localStorage on every mount for apps that haven't toggled
  // anything. Only writes after a real state change.
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
      window.localStorage.setItem(storageKey(appName), String(darkMode));
    } catch {
      // ignore quota / privacy-mode failures
    }
  }, [appName, darkMode]);

  const setDarkMode = useCallback((next) => {
    setDarkModeState((prev) => (typeof next === 'function' ? next(prev) : !!next));
  }, []);

  const toggleDarkMode = useCallback(() => {
    setDarkModeState((prev) => !prev);
  }, []);

  const theme = useMemo(() => buildTheme(darkMode, themeOptions), [darkMode, themeOptions]);

  const contextValue = useMemo(
    () => ({ darkMode, toggleDarkMode, setDarkMode }),
    [darkMode, toggleDarkMode, setDarkMode],
  );

  return (
    <DarkModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </DarkModeContext.Provider>
  );
};

/**
 * Read dark-mode state and toggle helpers.
 *
 * Returns `null` when no DarkModeProvider is in the tree — callers should
 * treat null as "this app hasn't opted in" and render nothing (or fall back
 * to light-only behavior). This deliberate non-throwing behavior is what lets
 * AuthLayout render gracefully whether or not the app uses DarkModeProvider.
 *
 * @returns {{ darkMode: boolean, toggleDarkMode: () => void, setDarkMode: (v: boolean | (prev: boolean) => boolean) => void } | null}
 */
export const useDarkMode = () => useContext(DarkModeContext);

export default DarkModeProvider;
